import * as React from 'react'
import { Slot } from 'radix-ui'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../utils'
import { IceIcon, type IconName } from './ice-icon'
import { CollapsibleLabel, TooltipElement, type CollapseBreakpoint } from './tooltip'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-xs w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        success: 'bg-success text-background [a&]:hover:bg-success/90',
        destructive:
          'bg-destructive text-background [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        highlighter:
          'bg-highlighter text-secondary-foreground [a&]:hover:bg-highlighter/90 focus-visible:ring-highlighter/20 dark:focus-visible:ring-highlighter/40 dark:bg-highlighter/60',
        'mild-blue': 'bg-mild-blue text-foreground [a&]:hover:bg-mild-blue/90',
        /**
         * Outlined rounded-rect rather than a pill — the only variant that
         * overrides the base `rounded-full`, which `cn`'s tailwind-merge
         * resolves in favour of this `rounded-md`.
         */
        'mild-yellow':
          'bg-warning-light text-warning-secondary border border-warning/40 rounded-md [a&]:hover:bg-warning-light/80',
        grey: 'bg-sterling text-foreground [a&]:hover:bg-sterling/90',
        warning:
          'bg-warning text-foreground [a&]:hover:bg-warning/90 focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40 dark:bg-warning/60',
        'warning-secondary':
          'bg-warning-light text-warning-secondary [a&]:hover:bg-warning/90 focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40 dark:bg-warning/60',

        primary: 'bg-primary text-background [a&]:hover:bg-primary/90',
        /** AI-derived state — the plum used across the signature-detection surfaces. */
        ai: 'bg-ai text-background [a&]:hover:bg-ai/90',
      },
    },
    defaultVariants: {
      variant: 'success',
    },
  },
)

/**
 * Collapsed badges become a round icon-only pill. `aspect-square` plus symmetric
 * padding derives the size from the icon, so it stays circular whatever icon
 * size the call site uses; the breakpoint variant restores the default padding.
 *
 * Static per-breakpoint strings — Tailwind cannot generate class names from
 * runtime values. Call sites must not pass conflicting unprefixed `px-*`/`py-*`,
 * which would still apply while collapsed.
 */
const COLLAPSED_BADGE_CLASSES: Record<CollapseBreakpoint, string> = {
  sm: 'aspect-square p-1.5 sm:aspect-auto sm:px-1.5 sm:py-0.5',
  md: 'aspect-square p-1.5 md:aspect-auto md:px-1.5 md:py-0.5',
  lg: 'aspect-square p-1.5 lg:aspect-auto lg:px-1.5 lg:py-0.5',
  xl: 'aspect-square p-1.5 xl:aspect-auto xl:px-1.5 xl:py-0.5',
  '2xl': 'aspect-square p-1.5 2xl:aspect-auto 2xl:px-1.5 2xl:py-0.5',
}

type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    /** Icon rendered before `label`. Ignored unless `label` is set. */
    icon?: IconName
    /**
     * Badge text. Supply this instead of `children` to opt into `collapseBelow`
     * — the badge needs to know which part of its content is the label.
     */
    label?: React.ReactNode
    /**
     * Collapse to a round icon-only badge below this breakpoint, with a tooltip
     * carrying the label. Requires `label`. Off by default.
     *
     * The label stays in the accessibility tree while collapsed, so the badge
     * still reads correctly to screen readers.
     */
    collapseBelow?: CollapseBreakpoint
  }

function Badge({
  className,
  variant,
  asChild = false,
  icon,
  label,
  collapseBelow,
  children,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot.Root : 'span'
  // `collapseBelow` needs a `label` to collapse and to fill the tooltip
  const isCollapsible = !!collapseBelow && label !== undefined

  const badge = (
    <Comp
      data-slot='badge'
      className={cn(
        badgeVariants({ variant }),
        isCollapsible && COLLAPSED_BADGE_CLASSES[collapseBelow],
        className,
      )}
      {...props}
    >
      {label === undefined ? (
        children
      ) : (
        <>
          {/* text-base matches every hand-rolled icon+label badge in the apps */}
          {icon && <IceIcon name={icon} className='text-base' />}
          {/* A plain span when not collapsing, so an ambient CollapsibleLabel
              breakpoint from an enclosing TooltipElement can't collapse a badge
              that never opted in */}
          {isCollapsible ? (
            <CollapsibleLabel below={collapseBelow}>{label}</CollapsibleLabel>
          ) : (
            <span>{label}</span>
          )}
        </>
      )}
    </Comp>
  )

  if (!isCollapsible) return badge

  return (
    <TooltipElement text={label} collapseBelow={collapseBelow}>
      {badge}
    </TooltipElement>
  )
}

export { Badge, badgeVariants }
